import { IAddWalletParams, IAddWalletParamsPaymentRecord } from "@/api_framework/api_modals/payment";
import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { usePaymentMethodListAPI } from "@/app/hooks/api_hooks/payment/usePaymentMethodListAPI";
import { usePaymentWalletListAPI } from "@/app/hooks/api_hooks/payment/usePaymentWalletListAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CartHeaderComponent from "../../CartBillingComponent/CartHeaderComponent";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";
import { usePaymentUserWalletDepositAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletDepositAPI";
import { setInoviceList } from "@/app_redux/reducers/slices/auth/cart_slice";
import clsx from "clsx";
import { Switch } from "@headlessui/react";
import { IPOSProps, localizeQRExpiry, useOnlinePaymentPostAPI } from "@/app/hooks/api_hooks/payment/useOnlinePaymentPostAPI";
import { usePaymentWebSocket } from "@/app/hooks/api_hooks/payment/usePaymentWebSocket";
import { useOnlinePaymentValidateAPI } from "@/app/hooks/api_hooks/payment/useOnlinePaymentValidateAPI";
import OnlinePaymentCancelModal from "../../CartBillingComponent/OnlinePaymentCancelModal";
import { useOnlinePaymentDeleteAPI } from "@/app/hooks/api_hooks/payment/useOnlinePaymentDeleteAPI";
import SwitchOnlineToOfflineConfirmationModal from "../../CartBillingComponent/SwitchOnlineToOfflineConfirmationModal";
import { usePaymentUserUnsettledPaymentAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserUnsettledPaymentAPI";
import { usePaymentOnlineToOfflineGetListAPI } from "@/app/hooks/api_hooks/payment/usePaymentOnlineToOfflineGetListAPI";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useSocket } from "@/app/components/socket/SocketProvider";
import { QR_TIMEOUT_SECONDS } from "@/app/components/routes/auth_routes/CartManagement/BillingMirrorComponent/BillingSummaryComponent";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";

export interface IAddCredit {
  wallet_type_id: string;
  comments: string;
  payments: IAddWalletParamsPaymentRecord[];
}

const AddCreditComponent = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { desktop } = useAppSelector((state) => state.root.UIStates.sidebar);
  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const { forOnlyNumbersWithDecimal } = useFormValidations();
  const { customerId, customerDetails } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;
  const { execute: fetchPaymentMode, paymentMode, onlinePaymentMode, paymentMachine } = usePaymentMethodListAPI();
  const { execute: fetchWalletType, walletType } = usePaymentWalletListAPI();
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const { execute: addWalletAPI } = usePaymentUserWalletDepositAPI();
  const { execute: fetchUnsettledPayment, unsettledPayment } = usePaymentUserUnsettledPaymentAPI();
  const { execute: collectOnlinePayment } = useOnlinePaymentPostAPI();
  const { sendJsonMessage } = useSocket();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.ADVANCE_COLLECTION);
  const [qrExpiresAt, setQrExpiresAt] = useState<number | null>(null);
  const [qrSecondsLeft, setQrSecondsLeft] = useState(0);

  useLayoutEffect(() => {
    if (!qrExpiresAt) return;
    const tick = () => setQrSecondsLeft(Math.max(0, Math.floor((qrExpiresAt - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [qrExpiresAt]);

  const sendPaymentQREvent = (action: string, payload: Record<string, any> = {}) => {
    sendJsonMessage({ event_type: "payment_qr", action, ...payload });
    logVMSClick(VMS_CLICK_EVENTS.QR_MIRROR_EVENT_SENT, action, {
      event_action: action,
      client_id: customerId,
      order_id: payload.order_id,
      amount: payload.amount,
    });
    setQrExpiresAt(action === "QR_SHOW" ? (payload.expires_at ? payload.expires_at * 1000 : Date.now() + QR_TIMEOUT_SECONDS * 1000) : null);
    if (action === "QR_SHOW") {
      localStorage.removeItem("lastPresentedCartId");
      localStorage.setItem("mirrorQROrderId", payload.order_id);
    }
  };

  const isQRLive = (item: any) =>
    item?.payment_mode === "QR_PAYMENT" &&
    item?.order_status?.toLowerCase() === "pending" &&
    !!item?.image_base64 &&
    (!item?.expires_at || item.expires_at * 1000 > Date.now());

  const sendAdvanceQRShow = (item: any) => {
    const amount = parseFloat(item.amount);
    sendPaymentQREvent("QR_SHOW", {
      customer_id: customerId,
      clinic_id: mappedClinicId,
      order_id: item.order_id,
      amount,
      image_base64: item.image_base64,
      expires_at: item.expires_at,
      expires_in: item.expires_at ? Math.round(item.expires_at - Date.now() / 1000) : undefined,
      advance_amount: amount,
      billing_summary: {
        customer_name: customerDetails?.name,
        total_payable: amount,
        is_advance: true,
      },
    });
  };

  useEffect(() => {
    const handleFocus = () => {
      const pendingQR = (formHook.getValues("payments") || []).find(isQRLive);
      if (!pendingQR) return;
      const mirrorTaken =
        !!localStorage.getItem("lastPresentedCartId") || localStorage.getItem("mirrorQROrderId") !== pendingQR.order_id;
      if (mirrorTaken) sendAdvanceQRShow(pendingQR);
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [customerId, mappedClinicId, customerDetails, sendJsonMessage]);

  const { connect: connectWS, disconnect: disconnectWS } = usePaymentWebSocket({
    onCompleted: (msg) => {
      const payments = formHook.getValues("payments") || [];
      const index = payments.findIndex((p: any) => p.order_id === msg.order_id);
      if (index !== -1) {
        if (payments[index].payment_mode === "QR_PAYMENT") {
          sendPaymentQREvent("PAYMENT_SUCCESS", { order_id: msg.order_id, amount: parseFloat(payments[index].amount) });
        }
        removePaymentMethod(index);
      }
      onlinePaymentValidate(msg.order_id).then(() => {
        if (customerId) {
          setTimeout(() => {
            fetchUserWalletDetails(customerId);
          }, 1000);
        }
      });
    },
    onFailed: (msg) => {
      const payments = formHook.getValues("payments") || [];
      const index = payments.findIndex((p: any) => p.order_id === msg.order_id);
      if (index !== -1) {
        if (payments[index].payment_mode === "QR_PAYMENT") sendPaymentQREvent("PAYMENT_FAILED", { order_id: msg.order_id });
        updateOnlinePaymentMethod(index, {
          ...payments[index],
          amount: "0",
          order_status: "FAILED",
        });
      }
    },
  });
  const { execute: onlinePaymentValidate } = useOnlinePaymentValidateAPI();
  const { execute: deleteOnlinePayment } = useOnlinePaymentDeleteAPI();
  const { execute: fetchOfflinereason } = usePaymentOnlineToOfflineGetListAPI();
  const [initiate, setInitiate] = useState<boolean>(false);
  const [isOfflinePaymentEnabled, setIsOfflinePaymentEnabled] = useState(false);
  const [isOfflineConfirmationModal, setIsOfflineConfirmationModal] = useState(false);
  const [onlinePaymentOrderPending, setOnlinePaymentOrderPending] = useState(false);
  const [cancelOnlinePaymentInProgress, setCancelOnlinePaymentInProgress] = useState(false);
  const [onlinePaymentvalidation, setOnlinePaymentvalidation] = useState(false);
  const [initialOffline, setInitialOffline] = useState(false);
  const [defaultMachineId, setDefaultMachineId] = useState(null);
  const [offlineData, setOfflineData] = useState([]);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.displayName);
  const isDogyWorld = userLogInfo && userLogInfo.toLowerCase().includes("edcpos");

  const formHook = useForm<any>({
    mode: "onChange",
    defaultValues: {
      payments: [],
    },
  });

  const { control, formState } = formHook;
  const {
    fields: paymentFields,
    append: addOnlinePaymentMethod,
    remove: removePaymentMethod,
    update: updateOnlinePaymentMethod,
    replace: replaceOnlinePaymentMethod,
  } = useFieldArray({
    control,
    name: "payments",
  });
  const {
    fields: offlinePaymentFields,
    append: addOfflinePaymentMethod,
    remove: removeOfflinePaymentMethod,
    replace: replaceOffilePaymentMethod,
  } = useFieldArray({
    control,
    name: "offline_payments",
  });

  const isUPIorCard = (paymentMode: string) => {
    return paymentMode === "UPI" || paymentMode === "Card";
  };

  const onSubmit = (data: any) => {
    logVMSClick(VMS_CLICK_EVENTS.ADD_CREDIT_SUBMIT_CLICK, "Add Credit", {
      event_action: data?.wallet_type_id,
      client_id: customerId,
    });
    const isValidAmount = (amount) => parseFloat(amount || "0") !== 0;
    const offline_payments = formHook
      .getValues("offline_payments")
      ?.filter((item) => isValidAmount(item.amount))
      .map((item) => ({
        amount: parseFloat(item.amount),
        payment_mode: item.payment_mode,
        order_id: null,
        rrn: item.bank_ref_no || null,
      }));
    const onlinePayments = formHook
      .getValues("payments")
      ?.filter((item) => isValidAmount(item.amount))
      .map((item) => ({
        amount: parseFloat(item.amount),
        payment_mode: item.payment_mode,
        order_id: item?.order_id,
      }));
    // Merge both arrays with duplicates allowed
    const allPaymentMethod = [...offline_payments, ...onlinePayments];
    const constructedBody: IAddWalletParams = {
      wallet_type: data.wallet_type_id,
      comments: data.comments,
      clinic_id: mappedClinicId,
      payments_details_list: allPaymentMethod,
      clinic_name: mappedClinicName,
      source: "Invoice",
    };
    addWalletAPI(constructedBody, customerId).then(({ status }) => {
      if (status) {
        dispatch(setInoviceList([]));
        navigate(`/app/user-cart/credit-list/${customerId}?from_cart=true`);
      }
    });
  };

  useEffect(() => {
    if (unsettledPayment?.length > 0 && !initiate) {
      setInitiate(true);
      const payments = unsettledPayment.map(localizeQRExpiry);
      replaceOnlinePaymentMethod(payments);
      const pendingQR = payments.find(
        (item) => item?.payment_mode === "QR_PAYMENT" && item?.order_status === "PENDING" && item?.image_base64,
      );
      if (pendingQR && isQRLive(pendingQR)) sendAdvanceQRShow(pendingQR);
      else if (pendingQR?.expires_at) setQrExpiresAt(pendingQR.expires_at * 1000);
    }
  }, [unsettledPayment, initiate]);

  const didInitiateFetch = useRef(false);

  useEffect(() => {
    if (!didInitiateFetch.current && unsettledPayment?.length === 0 && defaultMachineId && customerDetails?.mobile) {
      addOnlinePaymentMethod({
        payments_method: null,
        amount: "0",
        order_status: "INITIATED",
        order_id: null,
        mobile_number: customerDetails?.mobile,
        machine_id: defaultMachineId,
      });
      didInitiateFetch.current = true;
    }
  }, [unsettledPayment?.length, defaultMachineId, customerDetails?.mobile]);

  useEffect(() => {
    fetchWalletType();
  }, []);

  useEffect(() => {
    if (customerId) {
      fetchUserWalletDetails(customerId);
      fetchUnsettledPayment(customerId);
    }
  }, [customerId]);

  const paymentMethodItems = useSelectMenuReducer(paymentMode, "payment_mode", "payment_mode");
  const onlinePaymentModeItems = useSelectMenuReducer(onlinePaymentMode, "payment_mode", "payment_mode");

  const restrictedTitles = [
    "Bad Debt",
    "CX Credit",
    "VBCredit",
    "QComm COD", // Note: it's not "QComm_COD", make sure the title matches
    "Payu",
    "Razor Pay",
    "Cheque",
    "ShopSe",
    "Blue7Vets Credit",
  ];

  // RBAC: BILLING_RESTRICTED_PAYMENT_METHODS — legacy roles: central_ops, central_ops_admin, super_admin
  const canUseRestrictedPaymentMethods = useFeatureAccess("BILLING_RESTRICTED_PAYMENT_METHODS");

  const visiblePaymentOptions = paymentMethodItems.filter(
    (item) => !restrictedTitles.includes(item.title) || canUseRestrictedPaymentMethods,
  );

  const walletTypeItem = useSelectMenuReducer(walletType, "wallet_name", "wallet_type");

  const totalAmountTopaid = () => {
    const onlinePayments = formHook.watch("payments") || []; // Default to empty array if payments is undefined
    const offlinePayments = formHook.watch("offline_payments") ?? []; // Default to empty array if payments is undefined
    const totalsOnline = onlinePayments.reduce((acc, item) => {
      return acc + (parseFloat(item?.amount) ?? 0); // Parse item amount or default to 0
    }, 0);
    const totalOffline =
      offlinePayments?.length > 0 &&
      offlinePayments?.reduce((acc, item) => {
        return acc + (parseFloat(item?.amount) ?? 0);
      }, 0);
    const totalAmount = totalsOnline;
    if (isOfflinePaymentEnabled) {
      return totalAmount + totalOffline; // Add usedAmount and isSnapmintValue to totals
    } else {
      return totalAmount; // Add usedAmount and isSnapmintValue to totals
    }
  };

  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activePollingOrderRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const MAX_RETRIES = 50;

  // WebSocket-based status tracking (replaces polling)
  const startPaymentTracking = (order_id: string) => {
    if (order_id) {
      connectWS(order_id);
    }
  };

  // Fallback polling (only if WebSocket fails)
  const pollPaymentStatus = (
    order_id: string,
    index: number,
    amount: string,
    paymentMode: string,
    mobile_number: string,
    machine_id: string,
  ) => {
    onlinePaymentValidate(order_id).then(({ status, message }) => {
      const normalizedMsg = message?.trim().toLowerCase();
      if (status) {
        if (paymentMode === "QR_PAYMENT" && ["completed", "failed"].includes(normalizedMsg)) {
          sendPaymentQREvent(normalizedMsg === "completed" ? "PAYMENT_SUCCESS" : "PAYMENT_FAILED", { order_id, amount: parseFloat(amount) });
        }
        if (normalizedMsg === "completed") {
          removePaymentMethod(index);
          setOnlinePaymentvalidation(false);
          if (customerId) {
            setTimeout(() => {
              fetchUserWalletDetails(customerId);
            }, 1000);
          }
        }
        if (normalizedMsg === "failed") {
          updateOnlinePaymentMethod(index, {
            payment_mode: paymentMode,
            amount: "0",
            order_status: "FAILED",
            order_id,
            mobile_number,
            machine_id,
          });
        }
        if (["completed", "failed"].includes(normalizedMsg)) {
          if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
          activePollingOrderRef.current = null;
          retryCountRef.current = 0;
          return;
        }
      } else {
        setOnlinePaymentvalidation(false);
      }
      if (retryCountRef.current >= MAX_RETRIES) {
        if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
        activePollingOrderRef.current = null;
        retryCountRef.current = 0;
        return;
      }
      retryCountRef.current += 1;
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = setTimeout(() => {
        pollPaymentStatus(order_id, index, amount, paymentMode, mobile_number, machine_id);
      }, 4000);
    });
  };

  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      if (paymentFields?.length > 0) {
        const pendingOrders = paymentFields.filter((order) => order?.order_status === "PENDING");
        if (pendingOrders.length > 0) {
          const order = pendingOrders[0];
          // Use WebSocket for real-time tracking
          startPaymentTracking(order.order_id);
          didMountRef.current = true;
        }
      }
    }
    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      disconnectWS();
    };
  }, [paymentFields]);

  //online payment POS
  const handelCollectPaymentOnline = (index: any) => {
    setOnlinePaymentOrderPending(true);
    const paymentMode = formHook.getValues(`payments.${index}.payment_mode`);
    const amount = formHook.getValues(`payments.${index}.amount`);
    const mobile_number = formHook.getValues(`payments.${index}.mobile_number`);
    const machineId = formHook.getValues(`payments.${index}.machine_id`);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_COLLECT_CLICK, "Collect Payment", {
      event_action: paymentMode,
      client_id: customerId,
      amount: parseFloat(amount),
    });
    const constructedBody: IPOSProps = {
      machine_id: machineId,
      amount: parseFloat(amount),
      clinic_id: mappedClinicId,
      invoice_ids: [],
      payment_mode: paymentMode,
      user_id: customerId,
      source: "ADVANCE_COLLECTION",
      mobile_number: mobile_number,
    };
    collectOnlinePayment(constructedBody).then(({ status, message, payment_status, image_base64, expires_at }) => {
      if (status) {
        const row = {
          payment_mode: paymentMode,
          amount: amount,
          order_status: payment_status,
          order_id: message,
          mobile_number: mobile_number,
          machine_id: machineId,
          image_base64: image_base64,
          ...(paymentMode === "QR_PAYMENT" ? { expires_at } : {}),
        };
        updateOnlinePaymentMethod(index, row);
        if (paymentMode === "QR_PAYMENT" && image_base64) sendAdvanceQRShow(row);
        // Use WebSocket for real-time tracking instead of polling
        startPaymentTracking(message);
        setOnlinePaymentOrderPending(false);
      } else {
        setOnlinePaymentOrderPending(false);
      }
    });
  };

  const handelOnlinePaymentValidate = (index: any, order_id: any) => {
    setOnlinePaymentvalidation(true);
    const paymentMode = formHook.getValues(`payments.${index}.payment_mode`);
    const amount = formHook.getValues(`payments.${index}.amount`);
    const mobile_number = formHook.getValues(`payments.${index}.mobile_number`);
    const machine_id = formHook.getValues(`payments.${index}.machine_id`);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_VALIDATE_CLICK, "Validate Payment", {
      event_action: paymentMode,
      client_id: customerId,
      order_id,
      amount: parseFloat(amount),
    });
    onlinePaymentValidate(order_id).then(({ status, message }) => {
      const normalizedMsg = message?.trim().toLowerCase();
      if (status) {
        if (paymentMode === "QR_PAYMENT" && ["completed", "failed"].includes(normalizedMsg)) {
          sendPaymentQREvent(normalizedMsg === "completed" ? "PAYMENT_SUCCESS" : "PAYMENT_FAILED", { order_id, amount: parseFloat(amount) });
        }
        if (normalizedMsg === "completed") {
          updateOnlinePaymentMethod(index, {
            payment_mode: paymentMode,
            amount: amount,
            order_status: message,
            order_id: order_id,
            mobile_number: mobile_number,
            machine_id,
          });
          setOnlinePaymentvalidation(false);
          if (customerId) {
            setTimeout(() => {
              fetchUserWalletDetails(customerId);
            }, 1000);
          }
          return;
        }
        updateOnlinePaymentMethod(index, {
          payment_mode: paymentMode,
          amount: amount,
          order_status: message,
          order_id: order_id,
          mobile_number: mobile_number,
          machine_id,
        });
        setOnlinePaymentvalidation(false);
      } else {
        setOnlinePaymentvalidation(false);
      }
    });
  };

  const handelCancelOnlinePayment = (index: any, order_id: any) => {
    setCancelOnlinePaymentInProgress(true);
    const paymentMode = formHook.getValues(`payments.${index}.payment_mode`);
    const mobile_number = formHook.getValues(`payments.${index}.mobile_number`);
    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_CANCEL_CLICK, "Cancel Payment", { event_action: paymentMode, client_id: customerId, order_id });
    deleteOnlinePayment(order_id).then(({ status, message }) => {
      if (status) {
        updateOnlinePaymentMethod(index, {
          payment_mode: paymentMode,
          amount: "0",
          order_status: "INITIATED",
          order_id: null,
          mobile_number: mobile_number,
        });
        if (paymentMode === "QR_PAYMENT") sendPaymentQREvent("QR_HIDE");
        setCancelOnlinePaymentInProgress(false);
      } else {
        setOnlinePaymentOrderStatus(null);
        setCancelOnlinePaymentInProgress(false);
      }
    });
  };

  const isAddMoreOnlinePayment = () => {
    const payments = formHook.watch("payments") ?? [];
    return payments.some(
      (item) => item?.order_status?.toLowerCase() === "pending" || item?.order_status?.toLowerCase() === "initiated",
    );
  };

  const isAddMoreOnlinePaymentWithoffile = () => {
    const payments = formHook.watch("payments") ?? [];
    if (isOfflinePaymentEnabled) {
      const ispp = payments.some((item) => item?.order_status?.toLowerCase() === "pending");
      const ioo = payments.some(
        (item) => item?.order_status?.toLowerCase() === "initiated" && parseFloat(item?.amount || 0),
      );
      const iof = payments.some((item) => item?.order_status?.toLowerCase() === "completed");
      if (ispp) {
        return true;
      } else if (ioo) {
        return false;
      } else if (iof) {
        return false;
      }
    } else {
      return payments.some(
        (item) => item?.order_status?.toLowerCase() === "pending" || item?.order_status?.toLowerCase() === "initiated",
      );
    }
  };

  const handelSwitchPaymenttype = () => {
    logVMSClick(VMS_CLICK_EVENTS.OFFLINE_PAYMENT_TOGGLE_CLICK, "Offline Payment", {
      event_action: isOfflinePaymentEnabled ? "OFF" : "ON",
      client_id: customerId,
    });
    if (!isOfflinePaymentEnabled) {
      setIsOfflineConfirmationModal(true);
      totalAmountTopaid();
    } else {
      totalAmountTopaid();
      setIsOfflinePaymentEnabled(false);
      replaceOffilePaymentMethod([]);
    }
  };

  useEffect(() => {
    fetchPaymentMode();
    fetchOfflinereason().then(({ status, message }) => {
      if (status) {
        setOfflineData(message);
      }
    });
  }, []);

  const handelRowAdd = () => {
    addOnlinePaymentMethod({
      payments_method: null,
      amount: "0",
      order_status: "INITIATED",
      order_id: null,
      mobile_number: customerDetails?.mobile,
      machine_id: defaultMachineId,
    });
  };

  useEffect(() => {
    if (onlinePaymentModeItems?.length === 0 && paymentMethodItems?.length > 0 && !initialOffline) {
      replaceOffilePaymentMethod([{ payment_mode: null, amount: "0" }]);
      setInitialOffline(true);
      setIsOfflinePaymentEnabled(true);
    }
  }, [onlinePaymentModeItems, paymentMethodItems, initialOffline]);

  const paymentMachineItems = useSelectMenuReducer(paymentMachine, "machine_value", "machine_id");

  useEffect(() => {
    if (paymentMachine?.length > 0) {
      setDefaultMachineId(paymentMachine[0]?.machine_id);
    }
  }, [paymentMachine]);

  return (
    <>
      <div className="px-2 py-2 h-full">
        <div className="bg-white px-2">
          <div className="flex justify-between items-center border-b pb-2 pt-4">
            <p className="text-base font-semibold pt-2">Wallet</p>
          </div>
          <CartHeaderComponent />
        </div>
        <form onSubmit={formHook.handleSubmit(onSubmit)}>
          <div>
            <div className="flex items-center gap-20 flex-row bg-white  p-3 ">
              <p className="text-xs">Wallet Type</p>
              <div className="w-1/6">
                <SearchableSelectMenu
                  errorMessages={[{ message: "Category is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("wallet_type_id", item.id);
                    }
                  }}
                  fieldError={formHook.formState.errors.wallet_type_id}
                  register={formHook.register("wallet_type_id", { required: true })}
                  selectItems={walletTypeItem}
                  placeholder="Select Category"
                  showTooltips={true}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={walletTypeItem?.filter((oc) => oc.title === formHook.watch("wallet_type_id"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800 w-10"
                  containerClassName="w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-20 flex-row bg-white  p-3 ">
              <p className="text-xs">Comment</p>
              <div className="w-full px-2">
                <textarea
                  style={{
                    overflowY: "scroll", // Always show vertical scrollbar
                    scrollbarWidth: "thin", // Works for Firefox
                  }}
                  rows={3}
                  className={clsx(`rounded-md w-full h-full  border-gray-300 text-xs `)}
                  placeholder="Add Comment if any..."
                  {...formHook.register(`comments`, { required: false, minLength: 1 })}
                />
              </div>
            </div>
            {onlinePaymentModeItems?.length > 0 && (
              <>
                <div className={`my-4 bg-white px-3  py-3 rounded-lg w-full"}`}>
                  <p className="text-sm font-semibold">Online Payment Details</p>
                  {paymentFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-6 items-center gap-4 py-4 border-b w-full">
                      <div className="flex  col-span-2 items-center gap-4 flex-row ">
                        <p className="text-xs whitespace-nowrap">Payment Mode</p>
                        <div className="w-[170px]">
                          <SearchableSelectMenu
                            disabled={
                              formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" ||
                              formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending"
                            }
                            errorMessages={[{ message: "Category is required", type: "required" }]}
                            onSelectItem={(item) => {
                              if (item) {
                                formHook.setValue(`payments.${index}.payment_mode`, item.id);
                              }
                            }}
                            fieldError={
                              formHook?.formState?.errors?.payments
                                ? formHook?.formState?.errors?.payments[index]
                                  ? formHook?.formState?.errors?.payments[index]?.payment_mode
                                  : null
                                : null
                            }
                            register={formHook.register(`payments.${index}.payment_mode`, {
                              required: formHook.getValues(`payments.${index}.amount`) > 0,
                            })}
                            selectItems={onlinePaymentModeItems}
                            placeholder="Select Payment Mode"
                            showTooltips={true}
                            showTypedErrors={false}
                            showDropdownIcon={true}
                            defaultSelected={
                              onlinePaymentModeItems?.filter(
                                (oc) => oc.id === formHook.watch(`payments.${index}.payment_mode`),
                              )[0]
                            }
                            listBoxClassName="w-full"
                            className="text-gray-800"
                            containerClassName="w-full"
                          />
                        </div>
                        {formHook.watch(`payments.${index}.payment_mode`) === "PAYMENT_LINK" ? (
                          <NumberInput
                            className="text-xs w-1/3 "
                            placeholder="Enter mobile number"
                            maxLength={10}
                            disabled={
                              formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" ||
                              formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending"
                            }
                            register={formHook.register(`payments.${index}.mobile_number`, {
                              required: false,
                              ...forOnlyNumbersWithDecimal.validations,
                              validate: (value) => {
                                // Trim leading zeros
                                const trimmedValue = String(value).replace(/^0+/, "") || "0";
                                formHook.setValue(`payments.${index}.mobile_number`, trimmedValue);
                                return null;
                              },
                            })}
                            fieldError={formState.errors.payments?.[index]?.mobile_number}
                            errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                          />
                        ) : formHook.watch(`payments.${index}.payment_mode`)?.startsWith("POS-") ? (
                          <SearchableSelectMenu
                            disabled={formHook.watch(`payments.${index}.order_status`) === "INITIATED" ? false : true}
                            errorMessages={[{ message: "", type: "required" }]}
                            onSelectItem={(item) => {
                              if (item) {
                                formHook.setValue(`payments.${index}.machine_id`, item.id);
                              }
                            }}
                            fieldError={
                              formHook?.formState?.errors?.payments
                                ? formHook?.formState?.errors?.payments[index]
                                  ? formHook?.formState?.errors?.payments[index]?.machine_id
                                  : null
                                : null
                            }
                            register={formHook.register(`payments.${index}.machine_id`)}
                            selectItems={paymentMachineItems}
                            placeholder="Payment Machine"
                            showTooltips={true}
                            showTypedErrors={false}
                            showDropdownIcon={true}
                            defaultSelected={
                              paymentMachineItems?.filter(
                                (oc) => oc.id === formHook.watch(`payments.${index}.machine_id`),
                              )[0]
                            }
                            listBoxClassName="w-full"
                            className="text-gray-800 w-20"
                            containerClassName="w-full"
                          />
                        ) : null}
                      </div>
                      <div className=" col-span-4 flex items-center gap-16">
                        <p className="flex items-center text-xs -ml-1">Amount : </p>
                        <div className="pl-4  w-[173px]">
                          <NumberInput
                            className="text-xs"
                            placeholder="Enter Amount"
                            maxLength={6}
                            disabled={
                              formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" ||
                              formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending"
                            }
                            register={formHook.register(`payments.${index}.amount`, {
                              required: false,
                              ...forOnlyNumbersWithDecimal.validations,
                              validate: (value) => {
                                // Trim leading zeros
                                const trimmedValue = String(value).replace(/^0+/, "") || "0"; // Remove all leading zeros, default to '0' if empty
                                // Assign trimmed value to form field
                                formHook.setValue(`payments.${index}.amount`, trimmedValue);
                                return null;
                              },
                            })}
                            fieldError={formState.errors.payments?.[index]?.amount}
                            errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                          />
                        </div>
                        <div className="flex items-center gap-4 ">
                          {formHook.watch(`payments.${index}.order_status`) === "INITIATED" && (
                            <button
                              type="button"
                              disabled={
                                !formHook.watch(`payments.${index}.payment_mode`) ||
                                onlinePaymentOrderPending ||
                                parseFloat(formHook.watch(`payments.${index}.amount`)) === 0
                              }
                              className={`cursor-pointer px-5 py-2 text-white rounded-lg border-transparent text-sm ${
                                !formHook.watch(`payments.${index}.payment_mode`) ||
                                parseFloat(formHook.watch(`payments.${index}.amount`)) === 0
                                  ? "bg-gray-300"
                                  : "bg-black"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handelCollectPaymentOnline(index);
                              }}
                            >
                              {onlinePaymentOrderPending ? <CircleSpinnerLoader h="20" w="113" /> : "Collect Payment"}
                            </button>
                          )}

                          <div className="flex ">
                            {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "failed" && (
                              <p
                                className={` p-1 px-2 text-[#FF0000] bg-red-100    font-medium rounded-full border-transparent text-xs uppercase`}
                              >
                                {formHook.watch(`payments.${index}.order_status`)}
                              </p>
                            )}
                            {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "completed" && (
                              <p
                                className={` p-1 px-2 text-[#149463] bg-green-100    font-medium rounded-full border-transparent text-xs uppercase`}
                              >
                                {formHook.watch(`payments.${index}.order_status`)}
                              </p>
                            )}
                            {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending" && (
                              <p
                                className={` p-1 px-2 text-[#E0A200] bg-yellow-100    font-medium rounded-full border-transparent text-xs uppercase`}
                              >
                                {formHook.watch(`payments.${index}.order_status`)}
                              </p>
                            )}
                          </div>
                          {formHook.watch(`payments.${index}.order_status`)?.toLowerCase() === "pending" && (
                            <div className="relative gap-5 flex items-center pl-2.5">
                              {" "}
                              {formHook.watch(`payments.${index}.payment_mode`) === "QR_PAYMENT" &&
                                formHook.watch(`payments.${index}.image_base64`) && (
                                  <div className="absolute bottom-full right-0 z-20 mb-2 flex w-max flex-col items-center rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                                    <p className="mb-1.5 whitespace-nowrap text-xs font-semibold">Scan & Pay ₹{formHook.watch(`payments.${index}.amount`)}</p>
                                    <img src={formHook.watch(`payments.${index}.image_base64`)} alt="Payment QR" className={`h-32 w-32 shrink-0 ${qrExpiresAt && qrSecondsLeft <= 0 ? "opacity-60 blur-[3px]" : ""}`} />
                                    {qrExpiresAt && (
                                      <p className={`mt-1.5 whitespace-nowrap text-xs font-medium ${qrSecondsLeft > 0 ? "text-gray-600" : "text-red-500"}`}>
                                        {qrSecondsLeft > 0
                                          ? `QR expires in ${String(Math.floor(qrSecondsLeft / 60)).padStart(2, "0")}:${String(qrSecondsLeft % 60).padStart(2, "0")}`
                                          : "QR Expired"}
                                      </p>
                                    )}
                                  </div>
                                )}
                              <button
                                type="button"
                                className=" cursor-pointer px-4 py-2 bg-black  text-white rounded-lg border-transparent text-sm "
                                onClick={() =>
                                  handelOnlinePaymentValidate(index, formHook.watch(`payments.${index}.order_id`))
                                }
                              >
                                {onlinePaymentvalidation ? <CircleSpinnerLoader h="20" w="125" /> : "Validate Payment"}
                              </button>
                              <OnlinePaymentCancelModal
                                handelCancelOnlinePayment={() =>
                                  handelCancelOnlinePayment(index, formHook.watch(`payments.${index}.order_id`))
                                }
                                cancelOnlinePaymentInProgress={cancelOnlinePaymentInProgress}
                                index={index}
                                orderId={formHook.watch(`payments.${index}.order_id`)}
                              />
                            </div>
                          )}

                          {(formHook.watch(`payments.${index}.order_status`) === "INITIATED" ||
                            formHook.watch(`payments.${index}.order_status`) === "FAILED") && (
                            <div className="pl-8">
                              {" "}
                              <button type="button" className="text-red-500 text-xs font-semibold flex justify-start">
                                <XCircleIcon
                                  className="w-5 h-5 cursor-pointer"
                                  onClick={() => {
                                    logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_REMOVE_CLICK, "Online", { client_id: customerId });
                                    removePaymentMethod(index);
                                  }}
                                />
                              </button>{" "}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="grid grid-cols-3 gap-1 items-center justify-between ">
                    {!isAddMoreOnlinePayment() ? (
                      <button
                        type="button"
                        className="text-[#0068FF] text-xs font-semibold flex items-center gap-2 my-2 w-fit"
                        onClick={() => {
                          logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_ADD_CLICK, "Online", { client_id: customerId });
                          handelRowAdd();
                        }}
                      >
                        <span className="p-1 h-4 items-center flex bg-[#0068FF] text-white rounded-full">+</span> Add
                        Another Online Payment Mode
                      </button>
                    ) : (
                      <p></p>
                    )}
                    {!isOfflinePaymentEnabled && (
                      <div className="pt-2">
                        <div className="flex items-center gap-16 flex-row ">
                          <p className="text-xs">Total paid :</p>
                          <p>&nbsp;{totalAmountTopaid()}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {isDogyWorld ? (
                    <button
                      className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-4 mt-2"
                      type="button"
                    >
                      <p>Enable Offline payment </p>
                      <Switch
                        checked={isOfflinePaymentEnabled}
                        onChange={() => {
                          handelSwitchPaymenttype();
                        }}
                        className={clsx(
                          isOfflinePaymentEnabled ? "bg-indigo-600" : "bg-gray-200",
                          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={clsx(
                            isOfflinePaymentEnabled ? "translate-x-5" : "translate-x-0",
                            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                          )}
                        />
                      </Switch>
                    </button>
                  ) : (
                    <RBACFlag actionId="CART_BILLING_OFFLINE_PAYMENT">
                      {" "}
                      <button
                        className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-4 mt-2"
                        type="button"
                      >
                        <p>Enable Offline payment </p>
                        <Switch
                          checked={isOfflinePaymentEnabled}
                          onChange={() => {
                            handelSwitchPaymenttype();
                          }}
                          className={clsx(
                            isOfflinePaymentEnabled ? "bg-indigo-600" : "bg-gray-200",
                            "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className={clsx(
                              isOfflinePaymentEnabled ? "translate-x-5" : "translate-x-0",
                              "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                            )}
                          />
                        </Switch>
                      </button>
                    </RBACFlag>
                  )}
                </div>
                <SwitchOnlineToOfflineConfirmationModal
                  open={isOfflineConfirmationModal}
                  setOpen={setIsOfflineConfirmationModal}
                  setIsOfflinePaymentEnabled={setIsOfflinePaymentEnabled}
                  isOfflinePaymentEnabled={isOfflinePaymentEnabled}
                  offlineData={offlineData}
                  addOfflinePaymentMethod={addOfflinePaymentMethod}
                />
              </>
            )}
            {isOfflinePaymentEnabled && (
              <div className={`my-4 bg-white px-3  py-3 rounded-lg w-full `}>
                <p className="text-sm font-semibold">Offline Payment Details</p>
                {offlinePaymentFields.map((field, index) => (
                  <div key={field.id} className="grid grid-cols-3 items-center gap-4 py-4 border-b w-full">
                    <div className="flex items-center gap-4 flex-row ">
                      <p className="text-xs whitespace-nowrap">Payment Mode</p>
                      <div className="w-[170px]">
                        <SearchableSelectMenu
                          errorMessages={[{ message: "Category is required", type: "required" }]}
                          onSelectItem={(item) => {
                            if (item) {
                              formHook.setValue(`offline_payments.${index}.payment_mode`, item.title);
                            }
                          }}
                          fieldError={
                            formHook?.formState?.errors?.offline_payments
                              ? formHook?.formState?.errors?.offline_payments[index]
                                ? formHook?.formState?.errors?.offline_payments[index]?.payment_mode
                                : null
                              : null
                          }
                          register={formHook.register(`offline_payments.${index}.payment_mode`, { required: true })}
                          selectItems={visiblePaymentOptions}
                          placeholder="Select Payment Mode"
                          showTooltips={true}
                          showTypedErrors={false}
                          showDropdownIcon={true}
                          defaultSelected={
                            visiblePaymentOptions?.filter(
                              (oc) => oc.title === formHook.watch(`offline_payments.${index}.payment_mode`),
                            )[0]
                          }
                          listBoxClassName="w-full"
                          className="text-gray-800 w-20"
                          containerClassName="w-full"
                        />
                      </div>
                      {isUPIorCard(formHook.watch(`offline_payments.${index}.payment_mode`)) && (
                        <div className="flex items-center gap-2 ml-2">
                          <p className="text-xs whitespace-nowrap">BankRefNo(RRN)</p>
                          <input
                            type="text"
                            placeholder="Enter RRN"
                            className="border border-gray-300 rounded-md px-2 py-1 text-xs w-32"
                            {...formHook.register(`offline_payments.${index}.bank_ref_no`, {
                              required: isUPIorCard(formHook.watch(`offline_payments.${index}.payment_mode`)),
                            })}
                          />
                          {formHook?.formState?.errors?.offline_payments?.[index]?.bank_ref_no && (
                            <span className="text-red-500 text-xs">*</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-16">
                      <p className="flex items-center text-xs">Amount : </p>
                      <span className="pl-3">
                        <NumberInput
                          className="text-xs"
                          placeholder="Enter Amount"
                          maxLength={6}
                          register={formHook.register(`offline_payments.${index}.amount`, {
                            required: true,
                            ...forOnlyNumbersWithDecimal.validations,
                            validate: (value: any) => {
                              const trimmedValue = value.replace(/^0+/, "") || "0"; // Remove all leading zeros, default to '0' if empty
                              formHook.setValue(`offline_payments.${index}.amount`, trimmedValue);
                              return null;
                            },
                          })}
                          fieldError={formState.errors.offline_payments?.[index]?.amount}
                          errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                        />
                      </span>
                    </div>
                    <button type="button" className="text-red-500 text-xs font-semibold">
                      <XCircleIcon
                        className="w-5 h-5 cursor-pointer"
                        onClick={() => {
                          logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_REMOVE_CLICK, "Offline", { client_id: customerId });
                          removeOfflinePaymentMethod(index);
                        }}
                      />
                    </button>
                  </div>
                ))}
                <div className="grid grid-cols-3 gap-1 items-center justify-between mb-10">
                  <button
                    type="button"
                    className="text-[#0068FF] text-xs font-semibold flex items-center gap-2 my-2 w-fit"
                    onClick={() => {
                      logVMSClick(VMS_CLICK_EVENTS.PAYMENT_ROW_ADD_CLICK, "Offline", { client_id: customerId });
                      addOfflinePaymentMethod({ payments_method: null, amount: "0" });
                    }}
                  >
                    <span className="p-1 h-4 items-center flex bg-[#0068FF] text-white rounded-full">+</span> Add
                    Offline Payment Mode
                  </button>
                  <div className="pt-2">
                    <div className="flex items-center gap-16 flex-row ">
                      <p className="text-xs">Total paid :</p>
                      <p>&nbsp;{totalAmountTopaid()}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={`fixed bottom-6 z-20 w-auto py-2 ${desktop?.show ? "left-[19rem]" : "left-[6rem]"} right-[1rem]`}
          >
            <button
              type="submit"
              className={`text-white ${totalAmountTopaid() > 0 && !isAddMoreOnlinePaymentWithoffile() ? "bg-[#193A6A]" : "bg-gray-400 cursor-not-allowed"} w-full py-2 rounded-lg font-semibold text-sm`}
              disabled={totalAmountTopaid() === 0 || isAddMoreOnlinePaymentWithoffile()}
            >
              Add Credit
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddCreditComponent;
